<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import {
    ancresDesLignesHorizontales,
    formatPuissanceFr,
    getClasseDimensionnante,
    getPlagesDenivele,
    getUnitePuissance,
    lectureRepartition,
    reperesDeLaRepartition,
    splitSerieTraces,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    etatsDeLaLegende,
    friseParMois,
    syntheseDeLaFrise,
    friseParJour,
    lignesCouvertureParClasse,
    lireExpositionDepassement,
    reserveDeClasseSurLAnnee,
} from "@modules/courtage/utils/optim-turpe-v2.qualite"
import {
    libellePlageMotriceIndeterminee,
    lireDepassementDuPropose,
    lireSiAlternatif,
    messageDeSynthese,
    plagesMotricesAEgalite,
} from "@modules/courtage/utils/optim-turpe-v2.diagnostic"
import { formatDateHeureParis } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { lireDureeDepassementDeLaPaire } from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import {
    LIBELLE_SCENARIO_ARBITRAGE,
    legendeDesClasses,
    libelleClasseHoraire,
    libelleDureeHeures,
    libelleTempsSous,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { lirePointesMesurees } from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { regleCroissanceDesPostes } from "@modules/courtage/utils/optim-turpe-v2.lexique"
import { lireStructureChoisie } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { messageEtudeSansDocument } from "@modules/courtage/utils/optim-turpe-v2.echec"
import { OptimTurpeV2Service } from "@modules/courtage/services/optim-turpe-v2.service"
import type {
    OptimTurpeV2DriverType,
    OptimTurpeV2Etude,
    OptimTurpeV2PlageDiagnostic,
    OptimTurpeV2PlageKey,
    OptimTurpeV2Serie,
} from "@modules/courtage/types/optim-turpe-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t: tBase } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // Vague wording du 14/10/2026 (partie 4, W57 à W61) : « poste »,
            // « puissance maximale atteinte », « Recommandé », « Vos
            // puissances », la monotone nommée, « estimées ».
            chartTitle:
                "Monotone de puissance en {plage} : part du temps passée sous chaque niveau",
            chartTitleSansPlage:
                "Monotone de puissance : part du temps passée sous chaque niveau",
            chartTitleC5: "Puissance souscrite, aujourd'hui et recommandée",
            chartTitleC5Choisie:
                "Puissance souscrite, aujourd'hui et vos puissances",
            view: {
                repartition: "Répartition",
                serie: "Puissance atteinte (10 min)",
            },
            // A2 (R10) : le titre de l'onglet complet, avec son poste.
            serieTitle: "Puissance atteinte toutes les 10 minutes, en {plage}",
            serieReading:
                "Chaque point = la puissance moyenne sur 10 min. Les périodes sans mesure sont estimées et tracées en pointillé gris.",
            serieLoading: "Chargement de la courbe…",
            // m4 de la revue : la série n'est pas conservée avec l'étude, elle
            // est recalculée à la demande. On le dit, daté.
            serieDatee:
                "Courbe recalculée le {date} sur les mesures détenues à cette date.",
            frise: {
                titre: "Mesures jour par jour",

                complet: "Jour complet",
                partiel: "Jour partiel",
                absent: "Jour sans mesure",
                inconnu: "Non compté",
            },
            serieError:
                "La courbe ne peut pas être affichée pour cette étude. La répartition reste consultable.",
            yAxis: "Puissance ({unite})",
            yAxisCdf: "% du temps",
            // Série UX du témoin (15/10/2026, 2.3 point 5) : les colonnes
            // « Souscrite aujourd'hui » et « Recommandé », déjà au Résultat,
            // quittent ce tableau.
            stripCols: {
                plage: "Poste",
                mesure: "Puissance maximale atteinte",
                jours: "Jours observés",
                joursValeur: "{observes} sur {offerts}",
                driver: "Profil",
            },
            stripDriving: "Poste qui fixe la puissance",
            afficherMonotone: "Afficher la monotone du poste {poste}",
            inviteMonotone: "Cliquez sur un poste pour voir sa monotone.",
            legend: {
                mesure: "Puissance mesurée",
                extrapole: "Estimée (mesure absente)",
                repartition: "Part du temps",
                propose: "Puissance souscrite recommandée",
                proposeChoisie: "Vos puissances",
                avant: "Puissance souscrite actuelle",
            },
            cats: {
                actuel: "Aujourd'hui",
                propose: "Recommandé",
                proposeChoisie: "Vos puissances",
            },
            noPlage: "Aucun poste analysé pour cette étude.",
            driver: {
                pic_isole: "Pic isolé",
                comportement_normal: "Pointe régulière",
                saisonnier: "Pointe saisonnière",
            },
            // DEV-2218 : un centile se dit par ce qu'il mesure (« 95 % du
            // temps sous »), jamais « P95 ».
            // 2.3 point 7 : le bloc statistique en deux groupes, le poste
            // dans le titre.
            statsTitre: "{profil} en {plage}",
            statsRepartition: "Répartition",
            statsConsequences: "Conséquences",
            stats: {
                max: "Puissance maximale atteinte : {value} {unite}",
                ecart: "La puissance maximale atteinte dépasse de {value} % le niveau dépassé seulement 1 % du temps",
                proche: "Proche de la puissance souscrite : {value}",
            },
            // DEV-2217 : la durée du scénario « avec », servie par le moteur.
            dureePaire:
                "Scénario « {scenario} » : environ {duree} de dépassement sur la période",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    etude: OptimTurpeV2Etude
    /**
     * Active la vue « Série » (DEV-789) : la série d'appels de puissance est récupérée à la demande
     * via l'API (recalcul courtage). False en mock/Storybook (pas de backend) → toggle masqué.
     */
    enableSerie?: boolean
    /**
     * Le « pourquoi » rédigé et servi avec l'étude, mots inchangés : il ouvre
     * « Pourquoi ces puissances » (maquette allégée, fusion avec l'ancienne
     * section « L'explication »).
     */
    explication?: string | null
}

type PlageChip = {
    key: OptimTurpeV2PlageKey
    diagnostic: OptimTurpeV2PlageDiagnostic
}

// ================================
// PROPS
// ================================
const props = withDefaults(defineProps<Props>(), {
    enableSerie: false,
    explication: null,
})

/** « 78 sur 78 » : les jours observés d'une classe, servis ; sinon « — ». */
function joursObserves(classe: string): string {
    const ligne = props.etude.qualite_donnees?.couverture_par_classe?.find(
        (l) => l?.classe === classe
    )
    const observes = ligne?.jours_observes
    const offerts = ligne?.jours_offerts
    return typeof observes === "number" && typeof offerts === "number"
        ? t("stripCols.joursValeur", { observes, offerts })
        : "—"
}

// Mode « ma propre structure » : le client a imposé la structure, la page ne
// la dit pas « proposée » (courtage#274, `recommandation.structure_choisie`).
const getStructureChoisie = computed(() => lireStructureChoisie(props.etude))
const MOTS_CHOISIS: Record<string, string> = {
    chartTitleC5: "chartTitleC5Choisie",
    "legend.propose": "legend.proposeChoisie",
    "cats.propose": "cats.proposeChoisie",
}
function t(cle: string, params?: Record<string, unknown>): string {
    const choisie = getStructureChoisie.value ? MOTS_CHOISIS[cle] : undefined
    return tBase(choisie ?? cle, params ?? {})
}

// Client API v2 — la vue « Série » récupère la courbe catégorisée à la demande (hors étude).
const optimTurpeV2Service = new OptimTurpeV2Service()

/** Série récupérée (lazy, au 1er passage en vue « Série ») + états de chargement. */
const fetchedSerie = ref<OptimTurpeV2Serie | null>(null)
const isFetchingSerie = ref<boolean>(false)
const serieError = ref<boolean>(false)
/**
 * Le texte servi quand la courbe est refusée parce que l'étude n'a pas de
 * résultat (DEV-2311, 409 nommé) ; `null` : message générique.
 */
const serieMessage = ref<string | null>(null)
/** L'instant où la série affichée a été recalculée (m4) : elle n'est pas conservée. */
const serieRecalculeeLe = ref<string | null>(null)

// ================================
// CONSTANTS
// ================================
/** Couleurs (alignées brand : mesuré cyan, dénivelé primaire, souscrit actuel orange). */
const COLOR_MESURE = "#06B6D4"
const COLOR_PROPOSE = "#151C5D"
const COLOR_AVANT = "#EA580C"
/** Gris ardoise pour les segments EXTRAPOLÉS (mesure absente, comblée) — DEV-789. */
const COLOR_EXTRAPOLE = "#94A3B8"

// ================================
// HELPERS
// ================================
const numberFr = formateurFr({ maximumFractionDigits: 0 })

/** Sévérité du chip de profil (driver) — neutre par défaut. */
function driverSeverity(
    type?: OptimTurpeV2DriverType
): "warn" | "info" | "secondary" {
    if (type === "pic_isole") return "warn"
    if (type === "saisonnier") return "info"
    return "secondary"
}

// ================================
// STATE
// ================================
/** Plage sélectionnée dans la bande (init sur la plage motrice). */
const selectedPlage = ref<OptimTurpeV2PlageKey | null>(null)
/** Le paragraphe complet du moteur, replié par défaut (revue 2, B3). */
const raisonnementOuvert = ref<boolean>(false)

/** Vue du graphe : répartition (CDF, défaut) ou série d'appels de puissance brute (DEV-789). */
const viewMode = ref<"repartition" | "serie">("repartition")

// ================================
// LIFECYCLE
// ================================
/**
 * Plotly fige sa largeur à l'init ; dans un conteneur flex pas encore mis en page
 * (Storybook, onglets), le tracé rend étroit. On relance quelques resize après le
 * layout pour qu'il remplisse la largeur. Sans effet en l'absence de graphe.
 */
const resizeTimers: number[] = []

/** Annule les relances en attente (démontage, ou avant d'en poser de nouvelles). */
function annulerRelancesResize(): void {
    for (const id of resizeTimers) window.clearTimeout(id)
    resizeTimers.length = 0
}

onMounted(() => {
    nextTick(() => window.dispatchEvent(new Event("resize")))
    annulerRelancesResize()
    for (const delay of [300, 800, 1500]) {
        resizeTimers.push(
            window.setTimeout(() => {
                // Composant jamais démonté (tests) : l'environnement peut
                // avoir disparu avant l'échéance.
                if (typeof window === "undefined") return
                window.dispatchEvent(new Event("resize"))
            }, delay)
        )
    }
})

// Sans cela, les relances se déclenchent après le démontage (et, en test,
// après la fermeture de jsdom : « window is not defined »).
onBeforeUnmount(annulerRelancesResize)

// ================================
// COMPUTED
// ================================
const getSegment = computed(() => props.etude.compteur?.segment ?? "")

const getIsC5 = computed(() => getSegment.value === "C5")

/** Unité de puissance souscrite selon le segment (C4/C5 = kVA, C2 = kW). */
const getUnite = computed(() => getUnitePuissance(props.etude))

/** Plages diagnostiquées (bande + chart), ordonnées (PLAGE_ORDER). */
const getPlageChips = computed<PlageChip[]>(() => {
    const plages = props.etude.diagnostic?.plages ?? {}
    return getPlagesDenivele(props.etude)
        .filter((key) => plages[key] !== undefined)
        .map((key) => ({
            key,
            diagnostic: plages[key] as OptimTurpeV2PlageDiagnostic,
        }))
})

/**
 * Plage motrice = la classe dimensionnante SERVIE par le moteur, la même que
 * l'en-tête et les cartes de scénario (revue du 25/09, écarts 8 et 9). La
 * preuve ne recalcule plus sa propre motrice : à égalité de dénivelé, son
 * choix pouvait différer de celui du moteur.
 */
/** Les classes à égalité quand le moteur ne désigne aucune plage motrice. */
const getPlagesAEgalite = computed(() => plagesMotricesAEgalite(props.etude))

const getDrivingPlage = computed<OptimTurpeV2PlageKey | null>(() => {
    // DEV-2182 (M11) : sur une égalité, aucune plage n'est dite motrice.
    if (getPlagesAEgalite.value) return null
    const plage = getClasseDimensionnante(props.etude)
    if (plage && getPlageChips.value.some((chip) => chip.key === plage)) {
        return plage
    }
    return getPlageChips.value[0]?.key ?? null
})

/** « Plage motrice indéterminée : HPB et HPH à égalité », ou `null`. */
const getPlageMotriceIndeterminee = computed(() =>
    getPlagesAEgalite.value
        ? libellePlageMotriceIndeterminee(getPlagesAEgalite.value)
        : null
)

/** Le message de synthèse du diagnostic, servi tel quel. */
const getMessageSynthese = computed(() => messageDeSynthese(props.etude))

/**
 * Plage active : la sélection de l'utilisateur, sinon la plage motrice, sinon
 * la première des classes à égalité (détaillée sans être dite motrice).
 */
const getActivePlage = computed<OptimTurpeV2PlageKey | null>(
    () =>
        selectedPlage.value ??
        getDrivingPlage.value ??
        getPlagesAEgalite.value?.find((key) =>
            getPlageChips.value.some((chip) => chip.key === key)
        ) ??
        getPlageChips.value[0]?.key ??
        null
)

/** Diagnostic de la plage active. */
const getActiveDiagnostic = computed<OptimTurpeV2PlageDiagnostic | null>(() => {
    const key = getActivePlage.value
    if (!key) return null
    return props.etude.diagnostic?.plages?.[key] ?? null
})

/** Dénivelé / puissance proposé pour la plage active (référence du graphique). */
const getProposeKva = computed<number | null>(() => {
    if (getIsC5.value) {
        return (
            props.etude.meilleure?.structure_tarifaire
                ?.puissance_souscrite_soutirage?.puissance ?? null
        )
    }
    return getActiveDiagnostic.value?.souscrit_propose_kva ?? null
})

/** Puissance souscrite ACTUELLE pour la plage active (référence « avant »). */
const getAvantKva = computed<number | null>(() => {
    const struct = props.etude.actuelle?.structure_tarifaire
    if (getIsC5.value) {
        return struct?.puissance_souscrite_soutirage?.puissance ?? null
    }
    const key = getActivePlage.value
    if (!key) return null
    return struct?.puissance_souscrite_soutirage?.denivele?.[key] ?? null
})

/** Pointe mesurée (max) pour la plage active, sinon le max global mesuré. */
const getMaxKva = computed<number | null>(() => {
    const key = getActivePlage.value
    if (key) {
        const fromStats = getActiveDiagnostic.value?.stats_kw?.max
        if (typeof fromStats === "number") return fromStats
        const fromPointes = lirePointesMesurees(props.etude)?.[key]
        if (typeof fromPointes === "number") return fromPointes
    }
    const pointes = Object.values(
        lirePointesMesurees(props.etude) ?? {}
    ).filter((v): v is number => typeof v === "number")
    return pointes.length > 0 ? Math.max(...pointes) : null
})

/** Titre du graphique selon le segment / la plage. */
const getChartTitle = computed(() =>
    getIsC5.value
        ? t("chartTitleC5")
        : getActivePlage.value
          ? t("chartTitle", { plage: plageEnClair(getActivePlage.value) })
          : t("chartTitleSansPlage")
)

/** 05/10/2026 : la classe en clair dans un titre, jamais son code seul. */
function plageEnClair(plage: string | null | undefined): string {
    return plage ? libelleClasseHoraire(plage) : ""
}

/** Bande compacte : une ligne par plage (label, dénivelé proposé, pointe, profil). */
/** La légende du tableau des classes (05/10/2026). */
const getLegendeStrip = computed(() =>
    legendeDesClasses(getStripRows.value.map((row) => row.key))
)

/**
 * Le graphique n'a de sens qu'en C5, sur une plage diagnostiquée, ou quand la
 * série des appels de puissance peut se charger.
 */
const getMontreGraphique = computed(
    () => getIsC5.value || getStripRows.value.length > 0 || getHasSerie.value
)

const getStripRows = computed(() =>
    getPlageChips.value.map((chip) => ({
        key: chip.key,
        joursDisplay: joursObserves(chip.key),
        // Revue témoin du 15/10/2026 (D8) : la puissance maximale atteinte
        // s'écrit comme au Résultat (67,83 kVA), jamais arrondie à 68.
        mesureDisplay:
            typeof chip.diagnostic.stats_kw?.max === "number"
                ? formatPuissanceFr(
                      chip.diagnostic.stats_kw.max,
                      getUnite.value
                  )
                : "—",
        driverType: chip.diagnostic.driver?.type,
        driverLabel: chip.diagnostic.driver?.type
            ? t(`driver.${chip.diagnostic.driver.type}`)
            : "",
        isDriving: chip.key === getDrivingPlage.value,
        isActive: chip.key === getActivePlage.value,
    }))
)

/**
 * Points de la fonction de répartition (CDF) interpolés depuis les percentiles MESURÉS
 * (p50/p95/p99/max) — le backend n'expose pas l'histogramme fin, mais ces points suffisent à
 * lire « X % du temps sous tel niveau ». Tri croissant + collapse des x dupliqués (p50 = 0…).
 */
const getCdfPoints = computed<{ x: number; y: number }[]>(() => {
    const s = getActiveDiagnostic.value?.stats_kw
    if (!s) return []
    const raw = [
        { x: 0, y: 0 },
        { x: s.p50, y: 50 },
        { x: s.p95, y: 95 },
        { x: s.p99, y: 99 },
        { x: s.max, y: 100 },
    ].filter((p) => typeof p.x === "number")
    raw.sort((a, b) => a.x - b.x)
    const pts: { x: number; y: number }[] = []
    for (const p of raw) {
        const last = pts[pts.length - 1]
        if (last && last.x === p.x) last.y = Math.max(last.y, p.y)
        else pts.push({ ...p })
    }
    return pts
})

/** % du temps couvert par une puissance donnée (interpolé sur la CDF). */
function couverturePct(x: number | null): number | null {
    const pts = getCdfPoints.value
    if (pts.length === 0 || typeof x !== "number") return null
    if (x <= pts[0].x) return pts[0].y
    for (let i = 1; i < pts.length; i++) {
        if (x <= pts[i].x) {
            const a = pts[i - 1]
            const b = pts[i]
            const ratio = b.x === a.x ? 1 : (x - a.x) / (b.x - a.x)
            return Math.round(a.y + ratio * (b.y - a.y))
        }
    }
    return 100
}

/**
 * Phrase de lecture de la répartition, conditionnée à la situation RÉELLE du
 * souscrit actuel devant la pointe mesurée (revue du 25/09, écart 7) : « au-dessus
 * de tout ce qui est mesuré » était un gabarit, faux sur un C2 à 400 kW souscrits
 * pour 589 kW mesurés.
 */
const getReadingText = computed(() => {
    const avant = getAvantKva.value
    return lectureRepartition(
        {
            proposePct: couverturePct(getProposeKva.value),
            propose: getProposeKva.value,
            avant,
            avantPct: couverturePct(avant),
            pointe: getMaxKva.value,
            unite: getUnite.value,
        },
        getStructureChoisie.value
    )
})

/**
 * Graphique de PREUVE (DEV-783) — 100 % données mesurées du diagnostic, sans fetch dapi.
 * Pour une plage : FONCTION DE RÉPARTITION (CDF) « % du temps sous tel niveau », avec le dénivelé
 * proposé et le souscrit actuel en repères verticaux (le client lit la couverture sans quantile).
 * Pour le C5 (puissance unique, sans diagnostic par plage) : comparaison souscrit actuel / proposé.
 */
const getGraph = computed(() => {
    const unite = getUnite.value
    const propose = getProposeKva.value
    const avant = getAvantKva.value
    const pts = getCdfPoints.value

    const baseLayout = {
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        font: { family: "Inter, sans-serif" },
    }

    // C5 / pas de stats par plage : comparaison simple avant → proposé + max mesuré.
    if (pts.length === 0) {
        const cats = [t("cats.actuel"), t("cats.propose")]
        const data: object[] = [
            {
                x: cats,
                y: [avant ?? 0, propose ?? 0],
                type: "bar",
                marker: { color: COLOR_MESURE },
                width: 0.5,
                hovertemplate: `%{x}: %{y} ${unite}<extra></extra>`,
            },
        ]
        if (typeof getMaxKva.value === "number") {
            data.push({
                x: cats,
                y: cats.map(() => getMaxKva.value),
                type: "scatter",
                mode: "lines",
                name: t("legend.avant"),
                line: { color: COLOR_AVANT, dash: "dot", width: 1.5 },
            })
        }
        return {
            data,
            layout: {
                ...baseLayout,
                margin: { t: 24, r: 16, b: 40, l: 56 },
                showlegend: false,
                xaxis: { showgrid: false, fixedrange: true },
                yaxis: {
                    title: { text: t("yAxis", { unite }) },
                    rangemode: "tozero",
                    fixedrange: true,
                },
                bargap: 0.5,
            },
        }
    }

    // CDF : x = puissance, y = % du temps en-dessous.
    const xs = pts.map((p) => p.x)
    const ys = pts.map((p) => p.y)
    const xMaxCourbe = pts[pts.length - 1].x
    if (typeof avant === "number" && avant > xMaxCourbe) {
        xs.push(avant)
        ys.push(100)
    }
    // Revue témoin du 15/10/2026 (D9, D10) : l'axe couvre les deux repères,
    // et leurs étiquettes courtes s'ancrent selon l'ordre des valeurs.
    const cadre = reperesDeLaRepartition(xMaxCourbe, propose, avant)

    const shapes: object[] = []
    const annotations: object[] = []
    const vline = (
        x: number,
        color: string,
        dash: string,
        label: string,
        anchor: "left" | "right"
    ): void => {
        shapes.push({
            type: "line",
            x0: x,
            x1: x,
            y0: 0,
            y1: 100,
            line: { color, width: 1.5, dash },
        })
        annotations.push({
            x,
            y: 100,
            yanchor: "bottom",
            xanchor: anchor,
            text: `${label} ${formatPuissanceFr(x, unite)}`,
            showarrow: false,
            font: { size: 10, color },
        })
    }
    for (const repere of cadre.reperes) {
        if (repere.cle === "propose") {
            vline(
                repere.x,
                COLOR_PROPOSE,
                "dash",
                t("cats.propose"),
                repere.ancre
            )
        } else {
            vline(repere.x, COLOR_AVANT, "dot", t("cats.actuel"), repere.ancre)
        }
    }

    return {
        data: [
            {
                x: xs,
                y: ys,
                type: "scatter",
                mode: "lines",
                name: t("legend.repartition"),
                line: { color: COLOR_MESURE, width: 2.5 },
                fill: "tozeroy",
                fillcolor: "rgba(6,182,212,0.12)",
                hovertemplate: `≤ %{x} ${unite} : %{y} % du temps<extra></extra>`,
            },
        ],
        layout: {
            ...baseLayout,
            margin: { t: 28, r: 40, b: 40, l: 48 },
            showlegend: false,
            shapes,
            annotations,
            xaxis: {
                title: { text: t("yAxis", { unite }) },
                showgrid: false,
                fixedrange: true,
                range: [0, cadre.borneAxe],
            },
            yaxis: {
                title: { text: t("yAxisCdf") },
                range: [0, 108],
                fixedrange: true,
                ticksuffix: " %",
            },
        },
    }
})

/** Vue « Série » proposée : activée par le parent (réel) ET étude persistée (id réel). */
/**
 * A1 (vague wording du 14/10/2026) : une étude sans courbe ne garde pas un
 * onglet qui ne peut rien montrer. Dès que la courbe se révèle absente (aucun
 * document servi), l'onglet disparaît et la vue revient à la répartition ;
 * il ne reste alors qu'une vue, donc aucun sélecteur.
 */
const serieAbsente = ref(false)
const getHasSerie = computed(
    () => props.enableSerie && !!props.etude._id && !serieAbsente.value
)

/** Série de la plage active (sinon classe implicite « _ » pour le C5) — depuis la série récupérée. */
const getActiveSerie = computed(() => {
    const serie = fetchedSerie.value
    if (!serie) return null
    const key = getActivePlage.value
    return (key ? serie.plages[key] : null) ?? serie.plages["_"] ?? null
})

/**
 * Récupère la série à la demande (1er passage en vue « Série »), puis la met en cache.
 * Recalcul courtage (lecture courbe + catégorisation) → on affiche un état de chargement.
 */
async function loadSerie(): Promise<void> {
    const analyseId = props.etude._id
    if (!analyseId || isFetchingSerie.value || fetchedSerie.value) return
    isFetchingSerie.value = true
    serieError.value = false
    serieMessage.value = null
    try {
        fetchedSerie.value =
            (await optimTurpeV2Service.getSerie(analyseId)) ?? null
        if (!fetchedSerie.value) {
            serieError.value = true
            serieAbsente.value = true
            viewMode.value = "repartition"
        } else serieRecalculeeLe.value = new Date().toISOString()
    } catch (error) {
        serieError.value = true
        serieMessage.value = messageEtudeSansDocument(error)
    } finally {
        isFetchingSerie.value = false
    }
}

/** Passage en vue « Série » → déclenche le chargement lazy (no-op si déjà chargée). */
watch(viewMode, (mode) => {
    if (mode === "serie") loadSerie()
})

/**
 * Graphe SÉRIE (DEV-789) : appels de puissance 10 min de la plage active dans le temps, sur la courbe
 * COMPLÉTÉE (cohérente avec le diagnostic / la CDF), avec le dénivelé proposé et le souscrit actuel en
 * repères horizontaux. Les points dont la mesure était absente (masque `comble`) sont tracés en pointillé
 * gris (« extrapolé ») — décision Mathieu : courbe continue mais honnête sur mesuré vs reconstruit.
 * `scattergl` : rendu fluide même à ~10 k points. `null` si pas de série chargée.
 */
const getSerieGraph = computed(() => {
    const serie = fetchedSerie.value
    const pts = getActiveSerie.value
    if (!serie || !pts || pts.idx.length === 0) return null
    const unite = getUnite.value
    const debutMs = Date.parse(serie.debut)
    const pasMs = (serie.pas_minutes || 10) * 60000
    const x = pts.idx.map((i) => new Date(debutMs + i * pasMs).toISOString())
    const propose = getProposeKva.value
    const avant = getAvantKva.value

    // Mesuré vs extrapolé (helper pur, testé) : ligne mesurée coupée sur les trous comblés + segments
    // extrapolés (pointillé gris) raccordés aux points-frontière mesurés. `connectgaps: false` rompt
    // chaque tracé sur ses `null`.
    const { measuredY, extrapY, hasCompleted } = splitSerieTraces(
        pts.kw,
        pts.comble ?? []
    )

    const shapes: object[] = []
    const annotations: object[] = []
    // N6 (19/10/2026) : la plus haute étiquette au-dessus de sa ligne, la
    // plus basse au-dessous ; deux valeurs proches ne se chevauchent plus.
    const ancres = ancresDesLignesHorizontales(propose, avant)
    const hline = (
        val: number,
        color: string,
        dash: string,
        label: string,
        yanchor: "top" | "bottom"
    ): void => {
        shapes.push({
            type: "line",
            xref: "paper",
            x0: 0,
            x1: 1,
            yref: "y",
            y0: val,
            y1: val,
            line: { color, width: 1.5, dash },
        })
        annotations.push({
            xref: "paper",
            x: 1,
            xanchor: "right",
            y: val,
            yref: "y",
            yanchor,
            text: `${label} ${numberFr.format(val)}`,
            showarrow: false,
            font: { size: 10, color },
        })
    }
    if (typeof propose === "number")
        hline(
            propose,
            COLOR_PROPOSE,
            "dash",
            t("legend.propose"),
            ancres.propose
        )
    if (typeof avant === "number")
        hline(avant, COLOR_AVANT, "dot", t("legend.avant"), ancres.avant)

    const data: object[] = [
        {
            x,
            y: measuredY,
            type: "scattergl",
            mode: "lines",
            name: t("legend.mesure"),
            line: { color: COLOR_MESURE, width: 1 },
            connectgaps: false,
            hovertemplate: `%{x|%d/%m %H:%M}<br>%{y} ${unite}<extra></extra>`,
        },
    ]
    if (hasCompleted) {
        data.push({
            x,
            y: extrapY,
            type: "scattergl",
            mode: "lines",
            name: t("legend.extrapole"),
            line: { color: COLOR_EXTRAPOLE, width: 1, dash: "dot" },
            connectgaps: false,
            hovertemplate: `%{x|%d/%m %H:%M}<br>%{y} ${unite} · ${t(
                "legend.extrapole"
            )}<extra></extra>`,
        })
    }

    return {
        data,
        layout: {
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            font: { family: "Inter, sans-serif" },
            // Marge haute élargie quand la légende « mesuré / extrapolé » est affichée.
            margin: { t: hasCompleted ? 44 : 28, r: 16, b: 40, l: 56 },
            showlegend: hasCompleted,
            legend: hasCompleted
                ? { orientation: "h", x: 0, y: 1.16, font: { size: 10 } }
                : undefined,
            shapes,
            annotations,
            xaxis: { showgrid: false, fixedrange: false },
            yaxis: {
                title: { text: t("yAxis", { unite }) },
                rangemode: "tozero",
                fixedrange: true,
            },
        },
    }
})

/** « Courbe recalculée le … » : dit sous la série, tant qu'elle n'est pas conservée. */
const getSerieDatee = computed(() =>
    serieRecalculeeLe.value
        ? t("serieDatee", {
              date: formatDateHeureParis(serieRecalculeeLe.value),
          })
        : null
)

/** Une ligne par classe de la formule, avec son verdict (DEV-2175). */
const getClasses = computed(() => lignesCouvertureParClasse(props.etude))

/** La frise jour par jour, quand le moteur la sert (DEV-2175). */
const getFrise = computed(() => friseParJour(props.etude))

/** La frise découpée par mois, avec la synthèse des jours (2.3 point 7). */
const getFriseParMois = computed(() =>
    getFrise.value ? friseParMois(getFrise.value) : []
)
const getSyntheseFrise = computed(() =>
    getFrise.value ? syntheseDeLaFrise(getFrise.value) : null
)

const COULEUR_JOUR: Record<string, string> = {
    complet: "bg-cyan-500",
    partiel: "bg-[var(--p-orange-400)]",
    absent: "bg-[var(--p-red-400)]",
    inconnu: "bg-surface-300",
}

/** Vue « série » demandée par l'utilisateur (indépendant du chargement/dispo). */
const getSerieMode = computed(() => viewMode.value === "serie")

/** Série affichée (demandée, chargée, et graphe construit). */
const getShowSerie = computed(
    () => getSerieMode.value && !!getSerieGraph.value && !isFetchingSerie.value
)

/** Titre du graphe selon la vue (série dès qu'on la demande, même en chargement). */
const getActiveTitle = computed(() =>
    getSerieMode.value
        ? t("serieTitle", { plage: plageEnClair(getActivePlage.value) })
        : getChartTitle.value
)

/** Phrase de lecture : série quand le graphe série est affiché, sinon lecture de la répartition. */
const getActiveReading = computed(() =>
    getSerieMode.value
        ? getShowSerie.value
            ? t("serieReading")
            : ""
        : getReadingText.value
)

/** Libellé du driver de la plage active, avec son poste (2.3 point 7). */
const getDriverLabel = computed(() => {
    const type = getActiveDiagnostic.value?.driver?.type
    if (!type) return ""
    const profil = t(`driver.${type}`)
    const plage = plageEnClair(getActivePlage.value)
    return plage ? t("statsTitre", { profil, plage }) : profil
})

/**
 * Les stats du callout driver (contrat DEV-762/#117 : stats_kw + driver
 * enrichi), en deux groupes (série UX du témoin, 2.3 point 7) : la
 * répartition (95 %, 99 %, maximum), puis les conséquences (écart,
 * proximité, dépassement).
 */
const getDriverStatsGroupes = computed<
    { titre: string; lignes: string[]; cle: string }[]
>(() => {
    const diag = getActiveDiagnostic.value
    if (!diag) return []
    const unite = getUnite.value
    const repartition: string[] = []
    const consequences: string[] = []
    if (diag.stats_kw) {
        repartition.push(
            libelleTempsSous(
                95,
                `${numberFr.format(diag.stats_kw.p95)} ${unite}`
            )
        )
        repartition.push(
            libelleTempsSous(
                99,
                `${numberFr.format(diag.stats_kw.p99)} ${unite}`
            )
        )
        repartition.push(
            t("stats.max", { value: numberFr.format(diag.stats_kw.max), unite })
        )
    }
    const driver = diag.driver
    if (driver) {
        if (typeof driver.ecart_max_sur_p99_pct === "number") {
            consequences.push(
                t("stats.ecart", {
                    value: numberFr.format(driver.ecart_max_sur_p99_pct),
                })
            )
        }
        if (typeof driver.duree_proche_du_souscrit_h === "number") {
            // DEV-2214 : 0,17 h ne s'écrit pas « 0 h ».
            consequences.push(
                t("stats.proche", {
                    value: libelleDureeHeures(
                        driver.duree_proche_du_souscrit_h
                    ),
                })
            )
        }
        // DEV-2182 (M4) : sous le dénivelé proposé, seules les clés neuves
        // disent un dépassement ; les anciennes décrivaient le niveau alternatif.
        const propose = lireDepassementDuPropose(
            diag,
            getStructureChoisie.value
        )
        if (propose) consequences.push(propose)
    }
    return [
        {
            cle: "repartition",
            titre: t("statsRepartition"),
            lignes: repartition,
        },
        {
            cle: "consequences",
            titre: t("statsConsequences"),
            lignes: consequences,
        },
    ].filter((groupe) => groupe.lignes.length > 0)
})

/**
 * Sous la plage motrice, la durée de dépassement du scénario « Assumer les
 * dépassements » servie par le moteur (DEV-2217) : c'est elle, et non le niveau
 * indicatif au 99ᵉ centile, qui dit ce que le scénario proposé laisse passer.
 */
const getDureePaire = computed<string | null>(() => {
    if (!getActivePlage.value || getActivePlage.value !== getDrivingPlage.value)
        return null
    const duree = libelleDureeHeures(
        lireDureeDepassementDeLaPaire(props.etude, getDrivingPlage.value)
    )
    return duree
        ? t("dureePaire", { scenario: LIBELLE_SCENARIO_ARBITRAGE.avec, duree })
        : null
})

/** Le niveau alternatif, sous son propre libellé « si vous souscriviez N ». */
const getSiAlternatif = computed(() =>
    lireSiAlternatif(getActiveDiagnostic.value, getUnite.value)
)

/**
 * La réserve de chaque classe sous-observée sur l'année, avec son levier,
 * rattachée à la ligne de la classe (DEV-2181, M3).
 */
const getClassesAvecReserve = computed(() =>
    (getClasses.value ?? []).map((ligne) => ({
        ...ligne,
        reserve: reserveDeClasseSurLAnnee(props.etude, ligne.classe),
    }))
)
/**
 * Maquette allégée : les jours observés passent dans le tableau des plages ;
 * ne reste écrite que la classe qui porte une réserve (sous-observée,
 * reconstituée…), avec son levier.
 */
const getClassesAReserve = computed(() =>
    getClassesAvecReserve.value.filter(
        (ligne) =>
            ligne.reserve !== null ||
            (ligne.verdict !== null && ligne.verdict !== "observee")
    )
)

/** L'exposition aux dépassements sur mesures reconstituées (DEV-2188, I5). */
const getExposition = computed(() => lireExpositionDepassement(props.etude))

/**
 * U9 (série UX du témoin, 3.9) : la monotone perd l'export PNG (l'appareil
 * photo) ; le rapport PDF est le livrable. Retiré pour ce graphique seulement,
 * par l'option de `PlotlyChart` : les autres graphiques de l'application
 * gardent leur barre d'outils.
 */
const BOUTONS_RETIRES_MONOTONE = ["toImage"]

/** La bande des postes se montre-t-elle (C4, C2, un poste diagnostiqué) ? */
const getAvecBande = computed(
    () => !getIsC5.value && getStripRows.value.length > 0
)

// ================================
// WATCHERS
// ================================
/** Réinitialise la sélection de plage + la série récupérée quand l'étude change. */
watch(
    () => props.etude,
    () => {
        selectedPlage.value = null
        viewMode.value = "repartition"
        fetchedSerie.value = null
        serieError.value = false
        serieMessage.value = null
        serieRecalculeeLe.value = null
        // A1 : l'absence de courbe vaut pour l'étude qui l'a révélée, pas pour
        // la suivante affichée dans le même composant.
        serieAbsente.value = false
    }
)

// ================================
// INTERACTIONS
// ================================
function onSelectPlage(key: OptimTurpeV2PlageKey): void {
    selectedPlage.value = key
}
</script>

<template>
    <!-- ================================ -->
    <!-- PREUVE — bande toutes-plages + répartition (CDF) focalisée + driver -->
    <!-- ================================ -->
    <div class="optim-turpe-v2-preuve flex min-w-0 flex-col gap-4">
        <!-- Série UX du témoin (15/10/2026, 2.3 point 5) : un seul
             paragraphe, la synthèse d'abord, puis le tableau, puis une seule
             note. -->
        <p
            v-if="getAvecBande || (explication && raisonnementOuvert)"
            class="text-sm text-primary-700"
            data-testid="optim-turpe-v2-preuve-paragraphe"
        >
            <template v-if="getAvecBande && getMessageSynthese">
                <span data-testid="optim-turpe-v2-synthese-diagnostic">{{
                    getMessageSynthese
                }}</span>
                {{ " " }}
            </template>
            <!-- Revue 2 en production (B3, I14, I37) : le paragraphe complet
                 du moteur attend derrière « Voir le raisonnement complet ». -->
            <template v-if="explication && raisonnementOuvert">
                <span data-testid="optim-turpe-v2-explication">{{
                    explication
                }}</span>
                {{ " " }}
            </template>
            <template v-if="getAvecBande && getPlageMotriceIndeterminee">
                <span data-testid="optim-turpe-v2-plage-motrice-indeterminee">{{
                    getPlageMotriceIndeterminee
                }}</span>
                {{ " " }}
            </template>
            <!-- Revue témoin du 15/10/2026 (D23, Q6) : un poste, jamais une
                 « plage » ; la ligne cliquable s'annonce. -->
            <span
                v-if="getAvecBande"
                data-testid="optim-turpe-v2-invite-monotone"
                >{{ t("inviteMonotone") }}</span
            >
        </p>
        <BaseButton
            v-if="explication"
            :label="
                raisonnementOuvert
                    ? 'Masquer le raisonnement complet'
                    : 'Voir le raisonnement complet'
            "
            size="small"
            text
            class="self-start p-0!"
            :aria-expanded="raisonnementOuvert"
            data-testid="optim-turpe-v2-voir-raisonnement"
            @click="raisonnementOuvert = !raisonnementOuvert"
        />
        <!-- Bande toutes-plages (C4 / C2) : un clic focalise le détail -->
        <section v-if="getAvecBande" class="flex flex-col gap-2">
            <div class="overflow-x-auto" data-legende-classes>
                <table class="w-full min-w-[40rem] text-sm">
                    <thead class="text-xs text-primary-500">
                        <tr>
                            <th class="px-2 py-1 text-left font-normal">
                                {{ t("stripCols.plage") }}
                            </th>
                            <th class="px-2 py-1 text-right font-normal">
                                {{ t("stripCols.mesure") }}
                            </th>
                            <th class="px-2 py-1 text-right font-normal">
                                {{ t("stripCols.jours") }}
                            </th>
                            <th class="px-2 py-1 text-left font-normal">
                                {{ t("stripCols.driver") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in getStripRows"
                            :key="row.key"
                            class="cursor-pointer border-t border-surface-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                            :class="
                                row.isActive
                                    ? 'bg-primary-50'
                                    : 'hover:bg-surface-100'
                            "
                            role="button"
                            tabindex="0"
                            :aria-pressed="row.isActive"
                            :aria-label="
                                t('afficherMonotone', { poste: row.key })
                            "
                            @click="onSelectPlage(row.key)"
                            @keydown.enter.prevent="onSelectPlage(row.key)"
                            @keydown.space.prevent="onSelectPlage(row.key)"
                        >
                            <td class="px-2 py-1">
                                <span class="font-medium text-primary-900">{{
                                    row.key
                                }}</span>
                                <BaseTag
                                    v-if="row.isDriving"
                                    severity="success"
                                    :value="t('stripDriving')"
                                    class="ml-2"
                                />
                            </td>
                            <td class="px-2 py-1 text-right text-primary-700">
                                {{ row.mesureDisplay }}
                            </td>
                            <td
                                class="px-2 py-1 text-right text-primary-700"
                                data-testid="optim-turpe-v2-jours-observes"
                            >
                                {{ row.joursDisplay }}
                            </td>
                            <td class="px-2 py-1">
                                <BaseTag
                                    v-if="row.driverLabel"
                                    :value="row.driverLabel"
                                    :severity="driverSeverity(row.driverType)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- Une seule note : la règle de croissance (écrite une fois,
                 selon le segment), puis la légende des postes. -->
            <p
                class="text-xs text-primary-600"
                data-testid="optim-turpe-v2-preuve-note"
            >
                <span data-testid="optim-turpe-v2-regle-denivele">{{
                    regleCroissanceDesPostes(getSegment)
                }}</span>
                <template v-if="getLegendeStrip">
                    {{ " " }}
                    <span data-testid="optim-turpe-v2-preuve-legende">{{
                        getLegendeStrip
                    }}</span>
                </template>
            </p>
        </section>

        <!-- Sans plage diagnostiquée (hors C5), ni titre ni graphique : le
             titre resterait coupé et le graphique vide ; seul le message de
             pied le dit. -->
        <template v-if="getMontreGraphique">
            <div class="flex flex-wrap items-center justify-between gap-2">
                <p class="text-sm font-medium text-primary-800">
                    {{ getActiveTitle }}
                </p>
                <!-- Bascule Répartition ↔ Série (DEV-789) — seulement si la série est dispo (run frais).
                     Série UX du témoin (2.2) : un contrôle segmenté de niveau
                     3, discret, jamais un bouton plein. -->
                <div
                    v-if="getHasSerie"
                    data-niveau="3"
                    class="inline-flex shrink-0 overflow-hidden rounded-md border border-surface-200 text-xs"
                    role="tablist"
                >
                    <button
                        type="button"
                        role="tab"
                        :aria-selected="viewMode === 'repartition'"
                        class="px-3 py-1.5 transition-colors"
                        :class="
                            viewMode === 'repartition'
                                ? 'bg-surface-100 font-medium text-primary-900 underline underline-offset-2'
                                : 'text-primary-600 hover:bg-surface-50'
                        "
                        @click="viewMode = 'repartition'"
                    >
                        {{ t("view.repartition") }}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        :aria-selected="viewMode === 'serie'"
                        class="px-3 py-1.5 transition-colors"
                        :class="
                            viewMode === 'serie'
                                ? 'bg-surface-100 font-medium text-primary-900 underline underline-offset-2'
                                : 'text-primary-600 hover:bg-surface-50'
                        "
                        @click="viewMode = 'serie'"
                    >
                        {{ t("view.serie") }}
                    </button>
                </div>
            </div>
            <p v-if="getActiveReading" class="-mt-1 text-xs text-primary-500">
                {{ getActiveReading }}
            </p>

            <!-- Répartition (CDF) ou série d'appels de puissance brute — 100 % données mesurées -->
            <div class="relative h-80 w-full overflow-hidden">
                <!-- Vue série : chargée à la demande (recalcul courtage) → loading / erreur / graphe -->
                <template v-if="getSerieMode">
                    <div
                        v-if="isFetchingSerie"
                        class="flex h-full w-full items-center justify-center gap-2 text-sm text-primary-500"
                    >
                        <BaseIcon icon="pi pi-spin pi-spinner" size="1rem" />
                        {{ t("serieLoading") }}
                    </div>
                    <BaseMessage
                        v-else-if="serieError || !getSerieGraph"
                        severity="info"
                    >
                        {{ serieMessage ?? t("serieError") }}
                    </BaseMessage>
                    <PlotlyChart
                        v-else
                        :graph="getSerieGraph"
                        :mode-bar-buttons-to-remove-extra="
                            BOUTONS_RETIRES_MONOTONE
                        "
                    />
                </template>
                <PlotlyChart
                    v-else
                    :graph="getGraph"
                    :mode-bar-buttons-to-remove-extra="BOUTONS_RETIRES_MONOTONE"
                />
            </div>

            <p
                v-if="getShowSerie && getSerieDatee"
                class="-mt-2 text-xs text-primary-500"
                data-testid="optim-turpe-v2-serie-datee"
            >
                {{ getSerieDatee }}
            </p>
        </template>

        <!-- Callout driver -->
        <div
            v-if="getDriverLabel"
            class="flex flex-col gap-2 rounded-md bg-primary-50 p-4"
        >
            <div class="flex items-center gap-2">
                <BaseIcon
                    icon="pi pi-chart-line"
                    class="text-primary"
                    size="1rem"
                />
                <span class="text-sm font-semibold text-primary-900">{{
                    getDriverLabel
                }}</span>
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
                <div
                    v-for="groupe in getDriverStatsGroupes"
                    :key="groupe.cle"
                    class="flex flex-col gap-1"
                    :data-testid="`optim-turpe-v2-stats-${groupe.cle}`"
                >
                    <p class="text-xs font-medium text-primary-800">
                        {{ groupe.titre }}
                    </p>
                    <ul class="flex list-disc flex-col gap-0.5 pl-4">
                        <li
                            v-for="(line, idx) in groupe.lignes"
                            :key="idx"
                            class="text-xs text-primary-700"
                        >
                            {{ line }}
                        </li>
                    </ul>
                </div>
            </div>
            <p
                v-if="getDureePaire"
                class="text-xs text-primary-700"
                data-testid="optim-turpe-v2-duree-paire"
            >
                {{ getDureePaire }}
            </p>
            <p
                v-if="getSiAlternatif"
                class="text-xs text-primary-700"
                data-testid="optim-turpe-v2-si-alternatif"
            >
                {{ getSiAlternatif }}
            </p>
        </div>

        <!-- DEV-2175 : la représentation de chaque classe, en jours, avec
                 son verdict (L3, L31). -->
        <section
            v-if="getClassesAReserve.length > 0"
            class="flex flex-col gap-1"
            data-testid="optim-turpe-v2-classes"
        >
            <div
                v-for="ligne in getClassesAReserve"
                :key="ligne.classe"
                :data-testid="`optim-turpe-v2-classe-${ligne.classe}`"
            >
                <p
                    class="text-xs"
                    :class="
                        ligne.verdict === 'observee' ||
                        ligne.verdict === 'hors-fenetre'
                            ? 'text-primary-700'
                            : 'text-[var(--p-orange-700)]'
                    "
                >
                    {{ ligne.texte }}
                </p>
                <!-- DEV-2181 (M3) : la classe sous-observée sur l'année,
                         dite sur sa ligne avec son levier. -->
                <template v-if="ligne.reserve">
                    <p class="text-xs text-[var(--p-orange-700)]">
                        {{ ligne.reserve.label }}
                    </p>
                    <p
                        v-if="ligne.reserve.levier"
                        class="text-xs text-[var(--p-orange-700)]"
                    >
                        {{ ligne.reserve.levier }}
                    </p>
                </template>
            </div>
        </section>

        <!-- DEV-2188 (I5) : l'exposition aux dépassements comptés sur des
                 mesures reconstituées, contre la structure qu'elle concerne. -->
        <p
            v-if="getExposition"
            class="text-xs text-[var(--p-orange-700)]"
            data-testid="optim-turpe-v2-exposition-depassement"
        >
            {{ getExposition.texte }}
        </p>

        <!-- DEV-2175 : la frise jour par jour, servie par le moteur. -->
        <section
            v-if="getFrise"
            class="flex flex-col gap-1"
            data-testid="optim-turpe-v2-frise"
        >
            <p class="text-sm font-medium text-primary-700">
                {{ t("frise.titre") }}
            </p>
            <!-- 2.3 point 7 : la synthèse en jours, puis une ligne par mois
                 avec son libellé. -->
            <p
                v-if="getSyntheseFrise"
                class="text-xs text-primary-600"
                data-testid="optim-turpe-v2-frise-synthese"
            >
                {{ getSyntheseFrise }}
            </p>
            <div class="flex flex-col gap-0.5">
                <div
                    v-for="mois in getFriseParMois"
                    :key="mois.mois"
                    class="flex items-center gap-2"
                    data-testid="optim-turpe-v2-frise-mois"
                >
                    <span
                        class="w-28 shrink-0 text-xs text-primary-600"
                        data-testid="optim-turpe-v2-frise-mois-libelle"
                        >{{ mois.libelle }}</span
                    >
                    <div class="flex flex-wrap gap-px">
                        <span
                            v-for="jour in mois.jours"
                            :key="jour.date"
                            class="inline-block h-3 w-1.5"
                            :class="COULEUR_JOUR[jour.etat]"
                            :title="jour.titre"
                            :aria-label="jour.titre"
                            :data-etat="jour.etat"
                            data-testid="optim-turpe-v2-frise-jour"
                        />
                    </div>
                </div>
            </div>
            <p class="flex flex-wrap gap-x-4 text-xs text-primary-500">
                <span
                    v-for="etat in etatsDeLaLegende(getFrise)"
                    :key="etat"
                    class="inline-flex items-center gap-1"
                >
                    <span
                        class="inline-block h-2 w-2"
                        :class="COULEUR_JOUR[etat]"
                    />
                    {{ t(`frise.${etat}`) }}
                </span>
            </p>
        </section>

        <BaseMessage
            v-if="getStripRows.length === 0 && !getIsC5"
            severity="info"
        >
            {{ t("noPlage") }}
        </BaseMessage>
    </div>
</template>
